import { useState, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Input,
  Label,
  useTheme,
} from '@lognai/synapse-design-system-v2';
import { Logo } from '../../components/Logo';
import { partnerGuestsApi } from '../../services/api/partner-guests';
import { PasswordRequirements } from '@lognai/plugin-components';
import { firstPasswordProblem } from '@lognai/plugin-sdk';
import { usePasswordPolicy } from '../../hooks/usePasswordPolicy';

export default function PartnerInviteAcceptPage() {
  const { theme } = useTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const policy = usePasswordPolicy();

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!token) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
          <Logo variant={theme === 'light' ? 'dark' : 'white'} className="h-12 w-auto" />
          <CardTitle className="text-xl font-normal">Invalid Invitation Link</CardTitle>
          <CardDescription>
            This invitation link is missing a token. Please use the full link from your email.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    // The password is optional here — it is only set when the invitation
    // creates a new account — but when one IS given it has to satisfy the same
    // policy the server enforces, which this form did not check at all
    // (QA ledger AUTH-008).
    if (password) {
      const problem = firstPasswordProblem(policy, password);
      if (problem) {
        setError(problem);
        return;
      }
    }
    setPending(true);
    setError(null);
    const r = await partnerGuestsApi.accept(token, password || undefined, fullName || undefined);
    setPending(false);
    if (r.success) {
      navigate('/login', { replace: true, state: { banner: 'Access granted. Sign in to continue.' } });
    } else if (r.code === 'EXPIRED') {
      navigate('/partner-invite/expired', { replace: true });
    } else {
      setError(r.error);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
        <Logo variant={theme === 'light' ? 'dark' : 'white'} className="h-12 w-auto" />
        <CardTitle className="text-xl font-normal">Accept Partner Invitation</CardTitle>
        <CardDescription>
          Fill in the fields below to accept your invitation. If you already have an account, you
          can leave these blank.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full-name">Full name (if creating an account)</Label>
            <Input
              id="full-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw">Password (only required for new accounts)</Label>
            <Input
              id="pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
              autoComplete="new-password"
            />
            {password.length > 0 && (
              <PasswordRequirements policy={policy} password={password} className="pt-1" />
            )}
          </div>
          {error && (
            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? 'Accepting…' : 'Accept Invitation'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
